import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ListIcon, XIcon } from "@phosphor-icons/react";
import {
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@stylingcv/ui";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { LocaleLink } from "@/client/components/locale-link";

import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { useAuthStore } from "@/client/stores/auth";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { i18n } = useLingui();
  const isRTL = i18n.locale.startsWith("ar");
  const { user } = useAuthStore();

  useEffect(() => {
    // Detect mobile
    const checkIsMobile = () => window.innerWidth < 1024;
    setIsMobile(checkIsMobile());

    // Enhanced scroll detection with multiple fallbacks
    const handleScroll = () => {
      const scrollY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop;
      const scrolled = scrollY > 20;
      setIsScrolled(scrolled);
    };

    // Set initial state
    handleScroll();

    // Multiple scroll event listeners for reliability
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });
    document.body.addEventListener("scroll", handleScroll, { passive: true });

    // Handle resize
    const handleResize = () => {
      setIsMobile(checkIsMobile());
      handleScroll();
    };

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
      document.body.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <motion.header
      data-scrolled={isScrolled}
      data-is-mobile={isMobile}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-white/80 shadow-[0_4px_30px_rgba(0,0,0,0.05)] backdrop-blur-xl dark:bg-gray-950/80"
          : "bg-transparent pt-2"
      }`}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0, transition: { delay: 0.1, duration: 0.4 } }}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className={`flex h-16 items-center justify-between gap-4 ${isRTL ? "flex-row-reverse" : ""}`}>
          <LocaleLink to="/" className="flex shrink-0 items-center">
            <Logo size={120} />
          </LocaleLink>

          <div
            className={`hidden items-center lg:flex ${isRTL ? "space-x-5 space-x-reverse" : "space-x-5"}`}
          >
            {[
              { label: t`Templates`, href: "/templates" },
              { label: t`Features`, href: "/features" },
              { label: t`Pricing`, href: "/pricing" },
            ].map((link) => (
              <LocaleLink
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isScrolled
                    ? "text-slate-600 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white"
                    : "text-[#3e3431]/75 hover:text-[#3e3431] dark:text-gray-200 dark:hover:text-white"
                }`}
              >
                {link.label}
              </LocaleLink>
            ))}
            <Button
              asChild
              variant="ghost"
              size="sm"
              className={`rounded-full font-medium transition-all duration-300 ${
                isScrolled
                  ? "!text-slate-700 hover:!bg-slate-100 hover:!text-slate-900 dark:!text-gray-300 dark:hover:!bg-gray-800/60 dark:hover:!text-white"
                  : "!text-[#3e3431] hover:!bg-black/5 dark:!text-gray-200 dark:hover:!bg-white/10"
              }`}
            >
              <LocaleLink to={user ? "/dashboard/resumes" : "/auth/login"} className="!text-current">
                {user ? t`Dashboard` : t`Sign in`}
              </LocaleLink>
            </Button>
            <Button
              asChild
              size="sm"
              className="stylion-cta relative overflow-hidden px-6 shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <LocaleLink to="/ai-generator" className="relative z-10 !text-[#3e3431]">
                {t`Create my resume`}
              </LocaleLink>
            </Button>
            <div
              className={`flex items-center ${isRTL ? "space-x-2.5 space-x-reverse" : "space-x-2"}`}
            >
              <LocaleSwitch
                className={`rounded-full transition-all duration-300 ${
                  isScrolled
                    ? "!text-slate-700 hover:!bg-slate-100 dark:!text-gray-300 dark:hover:!bg-gray-800/60"
                    : "!text-[#3e3431] hover:!bg-black/5 dark:!text-gray-200 dark:hover:!bg-white/10"
                }`}
              />
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`flex items-center lg:hidden ${
              isRTL ? "space-x-2.5 space-x-reverse" : "space-x-2"
            }`}
          >
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label={t`Open menu`}
                  className={`!ml-0 !mr-0 rounded-full transition-all duration-300 ${
                    isScrolled
                      ? "!text-slate-700 hover:!bg-slate-100 dark:!text-gray-300 dark:hover:!bg-gray-800/60 dark:hover:!text-white"
                      : "!text-[#3e3431] hover:!bg-black/5 dark:!text-gray-200 dark:hover:!bg-white/10"
                  } flex size-10 items-center justify-center`}
                >
                  <ListIcon size={24} />
                </button>
              </SheetTrigger>

              <SheetContent
                side={isRTL ? "left" : "right"}
                className="w-[280px] focus-visible:outline-none sm:w-[320px]"
                showClose={false}
              >
                <div className="flex h-full max-h-screen flex-col">
                  {/* Mobile Header with Close Button */}
                  <div
                    className={`flex shrink-0 items-center justify-between border-b p-4 ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <Logo size={100} />
                    <SheetClose asChild>
                      <Button size="icon" variant="ghost" className="size-10">
                        <XIcon size={20} />
                      </Button>
                    </SheetClose>
                  </div>

                  {/* Action Buttons */}
                  <div className="shrink-0 space-y-3 bg-slate-50 p-4 dark:bg-[#0f172a]">
                    <SheetClose asChild>
                      <Button
                        asChild
                        size="lg"
                        className="group relative h-14 w-full overflow-hidden rounded-full bg-[#0a1128] px-5 text-base font-medium text-white shadow-sm transition-all hover:bg-[#162347] !justify-start dark:bg-white dark:text-slate-900 dark:hover:bg-gray-100"
                      >
                        <LocaleLink
                          to="/ai-generator"
                          className="flex w-full items-center gap-3"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-yellow-400">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                          </svg>
                          {t`Create my resume`}
                        </LocaleLink>
                      </Button>
                    </SheetClose>

                    <SheetClose asChild>
                      <Button
                        asChild
                        variant="outline"
                        size="lg"
                        className="h-14 w-full rounded-full border border-gray-200 bg-white px-5 text-base font-medium text-[#2d68ff] shadow-sm hover:bg-gray-50 !justify-start dark:border-gray-800 dark:bg-gray-900 dark:text-blue-400 dark:hover:bg-gray-800"
                      >
                        <LocaleLink
                          to={user ? "/dashboard/resumes" : "/auth/login"}
                          className="flex w-full items-center gap-3"
                        >
                          {user ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-emerald-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-emerald-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                            </svg>
                          )}
                          {user ? t`Dashboard` : t`Sign in`}
                        </LocaleLink>
                      </Button>
                    </SheetClose>

        {/* Language Switch */}
                <div className="flex h-14 w-full items-center justify-center gap-6 rounded-full border border-gray-200 bg-transparent px-4 dark:border-gray-800">
                  <LocaleSwitch className="!size-6 !p-0 !border-none !bg-transparent hover:!bg-transparent focus:!ring-0 [&_img]:size-5 [&_img]:object-cover [&_img]:rounded-full" />
                </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
};
